import React, { useState } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { Camera, FileText, X, Upload, CheckCircle, AlertCircle, Send } from 'lucide-react';
import { ThemeColors, BorderRadius, Spacing, Typography, Shadows } from '../../styles/ThemeStyles';
import { FileStorageService } from '../../services/FileStorageService';
import { ImageCompressionService } from '../../services/ImageCompressionService';

interface ReplyFormProps {
  onSubmitReply: (text: string, media: File[], documents: File[], compressionEnabled?: boolean) => Promise<void>;
  onCancel: () => void;
  authorName?: string;
  storeId?: string;
}

const ReplyCard = styled.div`
  background-color: ${ThemeColors.background.cards};
  border-radius: ${BorderRadius.lg};
  box-shadow: ${Shadows.card};
  overflow: hidden;
  border: 1px solid ${ThemeColors.border.light};
  margin-top: ${Spacing.sm};
  margin-left: ${Spacing.md};
  
  @media (max-width: 768px) {
    margin-left: ${Spacing.xs};
  }
`;

const ReplyHeader = styled.div`
  padding: ${Spacing.sm} ${Spacing.md};
  background-color: ${ThemeColors.primary.light}33;
  border-bottom: 1px solid ${ThemeColors.border.light};
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const ReplyTitle = styled.h3`
  margin: 0;
  font-size: ${Typography.fontSize.md};
  font-weight: ${Typography.fontWeight.semiBold};
  color: ${ThemeColors.text.primary};
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  color: ${ThemeColors.text.secondary};
  padding: ${Spacing.xs};
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: ${BorderRadius.round};
  transition: all 0.2s ease;
  
  &:hover {
    background-color: ${ThemeColors.gray[200]};
    color: ${ThemeColors.text.primary};
  }
`;

const ReplyBody = styled.div`
  padding: ${Spacing.md};
`;

const TextArea = styled.textarea`
  width: 100%;
  padding: ${Spacing.sm};
  border: 1px solid ${ThemeColors.border.medium};
  border-radius: ${BorderRadius.md};
  font-size: ${Typography.fontSize.sm};
  color: ${ThemeColors.text.primary};
  transition: all 0.2s ease;
  min-height: 80px;
  resize: vertical;
  margin-bottom: ${Spacing.sm};
  font-family: ${Typography.fontFamily.primary};
  
  &:focus {
    outline: none;
    border-color: ${ThemeColors.primary.main};
    box-shadow: 0 0 0 2px ${ThemeColors.primary.light}40;
  }
  
  &::placeholder {
    color: ${ThemeColors.text.hint};
  }
`;

const ButtonsRow = styled.div`
  display: flex;
  gap: ${Spacing.sm};
  margin-bottom: ${Spacing.sm};
  flex-wrap: wrap;
`;

const UploadButton = styled.div`
  position: relative;
  overflow: hidden;
  flex: 1;
  min-width: 150px;
`;

const UploadButtonLabel = styled.label`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${Spacing.xs};
  padding: ${Spacing.xs} ${Spacing.sm};
  background-color: ${ThemeColors.background.paper};
  color: ${ThemeColors.text.primary};
  border: 1px solid ${ThemeColors.border.medium};
  border-radius: ${BorderRadius.md};
  cursor: pointer;
  transition: all 0.2s ease;
  font-size: ${Typography.fontSize.sm};
  font-weight: ${Typography.fontWeight.medium};
  
  @media (max-width: 768px) {
    padding: ${Spacing.xs};
    border-radius: ${BorderRadius.sm};
    font-size: ${Typography.fontSize.xs};
  }
  
  &:hover {
    background-color: ${ThemeColors.gray[100]};
  }
`;

const MediaPreviewContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${Spacing.xs};
  margin-bottom: ${Spacing.sm};
`;

const MediaPreviewItem = styled.div`
  position: relative;
  width: 80px;
  height: 80px;
  border-radius: ${BorderRadius.md};
  overflow: hidden;
  border: 1px solid ${ThemeColors.border.light};
`;

const MediaImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const RemoveButton = styled.button`
  position: absolute;
  top: ${Spacing.xs};
  right: ${Spacing.xs};
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background-color: ${ThemeColors.secondary.main}CC;
  color: white;
  border: none;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    background-color: ${ThemeColors.secondary.main};
  }
`;

const DocumentPreviewContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${Spacing.xs};
  margin-bottom: ${Spacing.sm};
`;

const DocumentItem = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: ${Spacing.xs} ${Spacing.sm};
  background-color: ${ThemeColors.gray[100]};
  border-radius: ${BorderRadius.md};
  border: 1px solid ${ThemeColors.border.light};
`;

const DocumentInfo = styled.div`
  display: flex;
  align-items: center;
  gap: ${Spacing.sm};
  flex: 1;
  overflow: hidden;
`;

const DocumentName = styled.span`
  font-size: ${Typography.fontSize.sm};
  color: ${ThemeColors.text.primary};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const FileSizeInfo = styled.span`
  font-size: ${Typography.fontSize.xs};
  color: ${ThemeColors.text.hint};
  margin-left: ${Spacing.xs};
`;

const ActionButtons = styled.div`
  display: flex;
  gap: ${Spacing.sm};
  justify-content: flex-end;
`;

const CancelButton = styled.button`
  padding: ${Spacing.xs} ${Spacing.sm};
  background-color: ${ThemeColors.background.paper};
  color: ${ThemeColors.text.primary};
  border: 1px solid ${ThemeColors.border.medium};
  border-radius: ${BorderRadius.md};
  font-weight: ${Typography.fontWeight.medium};
  font-size: ${Typography.fontSize.sm};
  cursor: pointer;
  transition: all 0.2s ease;
  
  @media (max-width: 768px) {
    padding: ${Spacing.xs};
    border-radius: ${BorderRadius.sm};
    font-size: ${Typography.fontSize.xs};
  }
  
  &:hover {
    background-color: ${ThemeColors.gray[100]};
  }
`;

const SubmitButton = styled.button<{ disabled?: boolean; uploading?: boolean }>`
  display: flex;
  align-items: center;
  gap: ${Spacing.xs};
  padding: ${Spacing.xs} ${Spacing.sm};
  background-color: ${props => props.disabled ? ThemeColors.gray[300] : ThemeColors.primary.main};
  color: white;
  border: none;
  border-radius: ${BorderRadius.md};
  font-weight: ${Typography.fontWeight.semiBold};
  font-size: ${Typography.fontSize.sm};
  cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
  transition: all 0.2s ease;
  
  @media (max-width: 768px) {
    padding: ${Spacing.xs};
    border-radius: ${BorderRadius.sm};
    font-size: ${Typography.fontSize.xs};
  }
  
  &:hover {
    background-color: ${props => props.disabled ? ThemeColors.gray[300] : ThemeColors.primary.dark};
  }
`;

const UploadStatus = styled.div<{ type: 'success' | 'error' }>`
  display: flex;
  align-items: center;
  gap: ${Spacing.xs};
  padding: ${Spacing.xs} ${Spacing.sm};
  border-radius: ${BorderRadius.md};
  background-color: ${props => props.type === 'success' ? ThemeColors.success.light : ThemeColors.error.light};
  color: ${props => props.type === 'success' ? '#000000' : ThemeColors.error.main};
  font-size: ${Typography.fontSize.sm};
  margin-bottom: ${Spacing.sm};
`;

const CompressionInfo = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: ${Spacing.sm};
  background-color: ${ThemeColors.primary.light};
  border-radius: ${BorderRadius.md};
  margin-bottom: ${Spacing.md};
`;

const CompressionLabel = styled.label`
  display: flex;
  align-items: center;
  gap: ${Spacing.sm};
  font-size: ${Typography.fontSize.sm};
  color: ${ThemeColors.text.primary};
  cursor: pointer;
`;

const CompressionToggle = styled.input.attrs({ type: 'checkbox' })`
  width: 18px;
  height: 18px;
  cursor: pointer;
`;

export const ReplyForm: React.FC<ReplyFormProps> = ({ 
  onSubmitReply,
  onCancel,
  authorName,
  storeId = ''
}) => {
  const { t } = useTranslation();
  const [replyText, setReplyText] = useState('');
  const [mediaFiles, setMediaFiles] = useState<File[]>([]);
  const [documents, setDocuments] = useState<File[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [uploadStatus, setUploadStatus] = useState<{ type: 'success' | 'error'; message: string } | null>(null);
  // Compression is always enabled - removed checkbox
  const compressionEnabled = true;
  
  const fileStorageService = FileStorageService.getInstance();
  const imageCompressionService = ImageCompressionService.getInstance();

  const handleMediaUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      const filesArray = Array.from(e.target.files);
      
      const validFiles: File[] = [];
      const errors: string[] = [];
      
      filesArray.forEach(file => {
        const validation = fileStorageService.validateFile(file, 10000); // No size limit for images
        if (validation.valid) {
          validFiles.push(file);
        } else {
          errors.push(`${file.name}: ${validation.error}`);
        }
      });
      
      if (errors.length > 0) {
        setUploadStatus({
          type: 'error',
          message: `Some files were rejected: ${errors.join(', ')}`
        });
      }
      
      if (validFiles.length > 0) {
        setMediaFiles([...mediaFiles, ...validFiles]);
        setUploadStatus({
          type: 'success',
          message: t('notes.imagesAddedSuccessfully', { count: validFiles.length }, `${validFiles.length} image(s) added successfully`)
        });
      }
      
      e.target.value = '';
    }
  };

  const handleDocumentUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      const filesArray = Array.from(e.target.files);
      
      const validFiles: File[] = [];
      const errors: string[] = [];
      
      filesArray.forEach(file => {
        const validation = fileStorageService.validateFile(file, 10000); // No size limit for documents
        if (validation.valid) {
          validFiles.push(file);
        } else {
          errors.push(`${file.name}: ${validation.error}`);
        }
      });
      
      if (errors.length > 0) {
        setUploadStatus({
          type: 'error',
          message: `Some files were rejected: ${errors.join(', ')}`
        });
      }
      
      if (validFiles.length > 0) {
        setDocuments([...documents, ...validFiles]);
        setUploadStatus({
          type: 'success',
          message: t('notes.documentsAddedSuccessfully', { count: validFiles.length }, `${validFiles.length} document(s) added successfully`)
        });
      }
      
      e.target.value = '';
    }
  };

  const handleSubmit = async () => {
    if (replyText.trim() === '') {
      setUploadStatus({
        type: 'error',
        message: t('notes.replyRequired', 'Please enter a reply')
      });
      return;
    }

    if (isSubmitting) {
      return;
    }

    setIsSubmitting(true);
    setUploadStatus(null);

    try {
      await onSubmitReply(replyText, mediaFiles, documents, compressionEnabled);
      
      // Reset form on success
      setReplyText('');
      setMediaFiles([]);
      setDocuments([]);
      onCancel();
    } catch (error) {
      console.error('Failed to submit reply:', error);
      setUploadStatus({
        type: 'error',
        message: t('notes.replyError', 'Failed to submit reply. Please try again.')
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleRemoveMedia = (index: number) => {
    const updatedMediaFiles = [...mediaFiles];
    updatedMediaFiles.splice(index, 1);
    setMediaFiles(updatedMediaFiles);
  };

  const handleRemoveDocument = (index: number) => {
    const updatedDocuments = [...documents];
    updatedDocuments.splice(index, 1);
    setDocuments(updatedDocuments);
  };

  return (
    <ReplyCard>
      <ReplyHeader>
        <ReplyTitle>{t('notes.replyTo', 'Reply to')} {authorName || t('notes.note', 'Note')}</ReplyTitle>
        <CloseButton onClick={onCancel}>
          <X size={18} />
        </CloseButton>
      </ReplyHeader>
      <ReplyBody>
        <TextArea
          value={replyText}
          onChange={(e) => setReplyText(e.target.value)}
          placeholder={t('notes.enterReply', 'Enter your reply...')}
        />

        {uploadStatus && (
          <UploadStatus type={uploadStatus.type}>
            {uploadStatus.type === 'success' ? <CheckCircle size={16} /> : <AlertCircle size={16} />}
            {uploadStatus.message}
          </UploadStatus>
        )}


        {mediaFiles.length > 0 && (
          <MediaPreviewContainer>
            {mediaFiles.map((file, index) => (
              <MediaPreviewItem key={`media-${index}`}>
                <MediaImage 
                  src={URL.createObjectURL(file)} 
                  alt={`Preview ${index}`} 
                />
                <RemoveButton onClick={() => handleRemoveMedia(index)}>
                  <X size={12} />
                </RemoveButton>
              </MediaPreviewItem>
            ))}
          </MediaPreviewContainer>
        )}

        {documents.length > 0 && (
          <DocumentPreviewContainer>
            {documents.map((file, index) => (
              <DocumentItem key={`doc-${index}`}>
                <DocumentInfo>
                  <FileText size={18} />
                  <DocumentName>
                    {file.name}
                    <FileSizeInfo>({fileStorageService.formatFileSize(file.size)})</FileSizeInfo>
                  </DocumentName>
                </DocumentInfo>
                <RemoveButton onClick={() => handleRemoveDocument(index)}>
                  <X size={12} />
                </RemoveButton>
              </DocumentItem>
            ))}
          </DocumentPreviewContainer>
        )}

        <ButtonsRow>
          <UploadButton>
            <UploadButtonLabel htmlFor="reply-media-upload">
              <Camera size={18} />
              {t('notes.uploadMedia', 'Upload Photo')}
              <input
                type="file"
                id="reply-media-upload"
                accept="image/*"
                onChange={handleMediaUpload}
                style={{ display: 'none' }}
                multiple
              />
            </UploadButtonLabel>
          </UploadButton>

          <UploadButton>
            <UploadButtonLabel htmlFor="reply-document-upload">
              <FileText size={18} />
              {t('notes.uploadDocument', 'Upload Document')}
              <input
                type="file"
                id="reply-document-upload"
                accept=".pdf,.doc,.docx,.xls,.xlsx,.txt"
                onChange={handleDocumentUpload}
                style={{ display: 'none' }}
                multiple
              />
            </UploadButtonLabel>
          </UploadButton>
        </ButtonsRow>

        <ActionButtons>
          <CancelButton onClick={onCancel}>
            {t('common.cancel')}
          </CancelButton>
          <SubmitButton 
            onClick={handleSubmit}
            disabled={isSubmitting || replyText.trim() === ''}
            uploading={isSubmitting}
          >
            {isSubmitting ? (
              <>
                <Upload size={16} />
                {t('notes.uploading')}
              </>
            ) : (
              <>
                <Send size={16} />
                {t('notes.sendReply', 'Send Reply')}
              </>
            )}
          </SubmitButton>
        </ActionButtons>
      </ReplyBody>
    </ReplyCard>
  );
};

